import type {
  CardReviewEvent,
  MeaningPracticeState,
  ReviewEvent,
  VocabularySnapshot,
  WrittenReviewEvent,
} from "../vocabulary/types.ts";
import { snapshotWithDataRevision } from "../vocabulary/mutations.ts";
import {
  cardTargetSnapshot,
  classifyCardPresentation,
  classifyPresentation,
  isCardClockBackward,
  isClockBackward,
  isDue,
  maxInstant,
  nextCardSchedule,
  nextSchedule,
  practiceStateFor,
  REVIEW_HISTORY_RETENTION,
  scheduleSnapshotOf,
} from "./scheduling.ts";

export type ApplyReviewEventFailure = {
  ok: false;
  reason:
    | "missing_meaning"
    | "revision_mismatch"
    | "stale_content"
    | "missing_exercise"
    | "clock_backward"
    | "unsupported_scheduler"
    | "causal_mismatch"
    | "stale_classification";
};

export type ApplyReviewEventSuccess = {
  ok: true;
  snapshot: VocabularySnapshot;
  duplicate: boolean;
};

export type ApplyReviewEventResult = ApplyReviewEventSuccess | ApplyReviewEventFailure;

function meaningExerciseIds(snapshot: VocabularySnapshot, meaningId: string): Set<string> {
  const set = snapshot.exerciseSets.find((item) => item.meaningId === meaningId);
  return new Set(set?.exercises.map((exercise) => exercise.id) ?? []);
}

function applyWrittenToState(state: MeaningPracticeState, event: WrittenReviewEvent): MeaningPracticeState {
  const classification = classifyPresentation(state, event.presentedAt);
  const schedule = nextSchedule(state, {
    eventKind: event.eventKind,
    outcome: event.firstAttemptOutcome,
    hintUsed: event.hintUsed,
    classification,
    completedAt: event.completedAt,
  });
  const flaggedExerciseIds =
    event.eventKind === "invalidation" && event.exerciseId
      ? state.flaggedExerciseIds.includes(event.exerciseId)
        ? state.flaggedExerciseIds
        : [...state.flaggedExerciseIds, event.exerciseId]
      : state.flaggedExerciseIds;
  const lastAssessmentAt =
    event.eventKind === "assessment" ? event.completedAt : state.lastAssessmentAt;
  const lastExerciseId = event.exerciseId ?? state.lastExerciseId;
  const lastExposureAt =
    event.eventKind === "invalidation" && event.firstAttemptOutcome == null
      ? state.lastExposureAt
      : event.completedAt;
  return {
    ...state,
    stage: schedule.stage,
    dueAt: schedule.dueAt,
    lastExposureAt,
    lastAssessmentAt,
    lastExerciseId,
    flaggedExerciseIds,
    stateRevision: state.stateRevision + 1,
  };
}

function pruneHistory(events: readonly ReviewEvent[], truncated: boolean): {
  reviewHistory: ReviewEvent[];
  historyTruncated: boolean;
} {
  if (events.length <= REVIEW_HISTORY_RETENTION) {
    return { reviewHistory: [...events], historyTruncated: truncated };
  }
  return {
    reviewHistory: events.slice(events.length - REVIEW_HISTORY_RETENTION),
    historyTruncated: true,
  };
}

function replacePracticeState(
  snapshot: VocabularySnapshot,
  nextState: MeaningPracticeState,
): MeaningPracticeState[] {
  return snapshot.practiceStates.some((state) => state.meaningId === nextState.meaningId)
    ? snapshot.practiceStates.map((state) => (state.meaningId === nextState.meaningId ? nextState : state))
    : [...snapshot.practiceStates, nextState];
}

function applyWrittenEvent(
  snapshot: VocabularySnapshot,
  event: WrittenReviewEvent,
): ApplyReviewEventResult {
  if (snapshot.schedulerState.schedulerVersion !== event.schedulerVersion) {
    return { ok: false, reason: "unsupported_scheduler" };
  }
  const meaning = snapshot.meanings.find((item) => item.id === event.meaningId);
  if (!meaning) {
    return { ok: false, reason: "missing_meaning" };
  }
  if (isClockBackward(snapshot.schedulerState.lastObservedAt, event.presentedAt, event.completedAt)) {
    return { ok: false, reason: "clock_backward" };
  }
  const current = practiceStateFor(snapshot, event.meaningId);
  if (current.stateRevision !== event.expectedStateRevision) {
    return { ok: false, reason: "revision_mismatch" };
  }
  if (event.eventKind !== "exposure") {
    if (meaning.contentRevision !== event.meaningContentRevision) {
      return { ok: false, reason: "stale_content" };
    }
    if (event.exerciseId) {
      const ids = meaningExerciseIds(snapshot, event.meaningId);
      if (!ids.has(event.exerciseId)) {
        return { ok: false, reason: "missing_exercise" };
      }
    }
  }

  const nextState = applyWrittenToState(current, event);
  const recorded: WrittenReviewEvent = {
    ...event,
    previousSchedule: scheduleSnapshotOf(current),
    resultingSchedule: scheduleSnapshotOf(nextState),
  };
  const lastPracticedMeaningId =
    event.eventKind === "exposure" ? snapshot.schedulerState.lastPracticedMeaningId : event.meaningId;
  const lastObservedAt = snapshot.schedulerState.lastObservedAt
    ? maxInstant(snapshot.schedulerState.lastObservedAt, event.completedAt)
    : event.completedAt;
  const history = pruneHistory([...snapshot.reviewHistory, recorded], snapshot.historyTruncated);

  return {
    ok: true,
    duplicate: false,
    snapshot: snapshotWithDataRevision(snapshot, {
      words: snapshot.words,
      meanings: snapshot.meanings,
      relations: snapshot.relations,
      exerciseSets: snapshot.exerciseSets,
      practiceStates: replacePracticeState(snapshot, nextState),
      schedulerState: {
        ...snapshot.schedulerState,
        lastPracticedMeaningId,
        lastObservedAt,
      },
      reviewHistory: history.reviewHistory,
      historyTruncated: history.historyTruncated,
    }),
  };
}

function cardEventShape(event: CardReviewEvent): ApplyReviewEventFailure | null {
  if (event.actionKind === "grade") {
    if (event.outcome == null || event.gradingMethod !== "self") {
      return { ok: false, reason: "causal_mismatch" };
    }
  } else if (event.outcome != null || event.gradingMethod != null) {
    return { ok: false, reason: "causal_mismatch" };
  }
  return null;
}

function classificationStillMatches(state: MeaningPracticeState, event: CardReviewEvent): boolean {
  const card = state.cardSchedules[event.direction];
  return (
    isDue(card.dueAt, event.presentedAt) === event.wasDue &&
    (card.lastAssessmentAt == null) === event.isFirstAssessment
  );
}

export function firstCardGradeEvent(snapshot: VocabularySnapshot, attemptId: string): CardReviewEvent | undefined {
  return snapshot.reviewHistory.find(
    (item): item is CardReviewEvent =>
      item.format === "cards" && item.attemptId === attemptId && item.actionKind === "grade",
  );
}

function validateCardClassification(
  snapshot: VocabularySnapshot,
  state: MeaningPracticeState,
  event: CardReviewEvent,
): ApplyReviewEventFailure | null {
  if (event.actionKind === "presentation") {
    const fresh = classifyCardPresentation(state, event.direction, event.presentedAt);
    if (
      fresh.wasDue !== event.wasDue ||
      fresh.wasSpaced !== event.wasSpaced ||
      fresh.isFirstAssessment !== event.isFirstAssessment
    ) {
      return { ok: false, reason: "stale_classification" };
    }
    return null;
  }
  if (event.actionKind === "revisit") {
    return null;
  }
  const priorGrade = event.actionKind === "grade" ? firstCardGradeEvent(snapshot, event.attemptId) : undefined;
  if (!priorGrade && !classificationStillMatches(state, event)) {
    return { ok: false, reason: "stale_classification" };
  }
  if (
    priorGrade &&
    (priorGrade.wasDue !== event.wasDue ||
      priorGrade.wasSpaced !== event.wasSpaced ||
      priorGrade.isFirstAssessment !== event.isFirstAssessment ||
      priorGrade.presentedAt !== event.presentedAt ||
      priorGrade.direction !== event.direction ||
      priorGrade.meaningId !== event.meaningId)
  ) {
    return { ok: false, reason: "stale_classification" };
  }
  const presentation = snapshot.reviewHistory.find(
    (item): item is CardReviewEvent =>
      item.format === "cards" && item.attemptId === event.attemptId && item.actionKind === "presentation",
  );
  if (
    presentation &&
    (presentation.presentedAt !== event.presentedAt ||
      presentation.wasDue !== event.wasDue ||
      presentation.wasSpaced !== event.wasSpaced ||
      presentation.isFirstAssessment !== event.isFirstAssessment ||
      presentation.direction !== event.direction)
  ) {
    return { ok: false, reason: "stale_classification" };
  }
  return null;
}

function applyCardToState(
  snapshot: VocabularySnapshot,
  state: MeaningPracticeState,
  event: CardReviewEvent,
): { state: MeaningPracticeState; schedulerPatch: VocabularySnapshot["schedulerState"] } {
  const direction = event.direction;
  const currentCard = state.cardSchedules[direction];
  let nextCard = currentCard;
  let lastExposureAt = state.lastExposureAt;
  const schedulerPatch = { ...snapshot.schedulerState };

  if (event.actionKind === "presentation") {
    const ordinal = snapshot.schedulerState.cardPresentationCounters[direction] + 1;
    nextCard = {
      ...currentCard,
      lastPresentedAt: event.presentedAt,
      presentationOrdinal: ordinal,
    };
    lastExposureAt = lastExposureAt == null ? event.actionAt : maxInstant(lastExposureAt, event.actionAt);
    schedulerPatch.cardPresentationCounters = {
      ...schedulerPatch.cardPresentationCounters,
      [direction]: ordinal,
    };
    schedulerPatch.lastPresentedCardMeaningIds = {
      ...schedulerPatch.lastPresentedCardMeaningIds,
      [direction]: event.meaningId,
    };
  } else if (event.actionKind === "reveal" || event.actionKind === "context" || event.actionKind === "revisit") {
    lastExposureAt = lastExposureAt == null ? event.actionAt : maxInstant(lastExposureAt, event.actionAt);
  } else if (event.actionKind === "grade" && event.outcome) {
    const priorGrade = firstCardGradeEvent(snapshot, event.attemptId);
    const baseline = priorGrade
      ? {
          ...currentCard,
          stage: priorGrade.previousTarget.stage,
          dueAt: priorGrade.previousTarget.dueAt,
          lastAssessmentAt: priorGrade.previousTarget.lastAssessmentAt,
        }
      : currentCard;
    const schedule = nextCardSchedule(baseline, {
      outcome: event.outcome,
      contextUsed: event.contextUsed,
      wasDue: event.wasDue,
      wasSpaced: event.wasSpaced,
      isFirstAssessment: event.isFirstAssessment,
      actionAt: event.actionAt,
    });
    nextCard = {
      ...currentCard,
      stage: schedule.stage,
      dueAt: schedule.dueAt,
      lastAssessmentAt: event.actionAt,
    };
  }

  schedulerPatch.lastObservedAt = schedulerPatch.lastObservedAt
    ? maxInstant(schedulerPatch.lastObservedAt, event.actionAt)
    : event.actionAt;

  return {
    schedulerPatch,
    state: {
      ...state,
      lastExposureAt,
      cardSchedules: {
        ...state.cardSchedules,
        [direction]: nextCard,
      },
      stateRevision: state.stateRevision + 1,
    },
  };
}

function applyCardEvent(snapshot: VocabularySnapshot, event: CardReviewEvent): ApplyReviewEventResult {
  const shape = cardEventShape(event);
  if (shape) {
    return shape;
  }
  if (snapshot.schedulerState.cardPolicyVersion !== event.cardPolicyVersion) {
    return { ok: false, reason: "unsupported_scheduler" };
  }
  const meaning = snapshot.meanings.find((item) => item.id === event.meaningId);
  if (!meaning) {
    return { ok: false, reason: "missing_meaning" };
  }
  if (
    isCardClockBackward({
      knownObservedAt: event.knownObservedAt,
      presentedAt: event.presentedAt,
      actionAt: event.actionAt,
      previousActionAt: event.previousActionAt,
    })
  ) {
    return { ok: false, reason: "clock_backward" };
  }
  if (event.previousActionId) {
    const previous = snapshot.reviewHistory.find((item) => item.eventId === event.previousActionId);
    if (previous) {
      if (previous.format !== "cards" || previous.attemptId !== event.attemptId) {
        return { ok: false, reason: "causal_mismatch" };
      }
      if (event.previousActionAt == null || previous.actionAt !== event.previousActionAt) {
        return { ok: false, reason: "causal_mismatch" };
      }
    }
  }
  const current = practiceStateFor(snapshot, event.meaningId);
  if (current.stateRevision !== event.expectedStateRevision) {
    return { ok: false, reason: "revision_mismatch" };
  }
  if (meaning.contentRevision !== event.meaningContentRevision) {
    return { ok: false, reason: "stale_content" };
  }
  const classification = validateCardClassification(snapshot, current, event);
  if (classification) {
    return classification;
  }

  const applied = applyCardToState(snapshot, current, event);
  const recorded: CardReviewEvent = {
    ...event,
    previousTarget: cardTargetSnapshot(current, event.direction),
    resultingTarget: cardTargetSnapshot(applied.state, event.direction),
  };
  const history = pruneHistory([...snapshot.reviewHistory, recorded], snapshot.historyTruncated);
  return {
    ok: true,
    duplicate: false,
    snapshot: snapshotWithDataRevision(snapshot, {
      words: snapshot.words,
      meanings: snapshot.meanings,
      relations: snapshot.relations,
      exerciseSets: snapshot.exerciseSets,
      practiceStates: replacePracticeState(snapshot, applied.state),
      schedulerState: applied.schedulerPatch,
      reviewHistory: history.reviewHistory,
      historyTruncated: history.historyTruncated,
    }),
  };
}

export function applyReviewEvent(snapshot: VocabularySnapshot, event: ReviewEvent): ApplyReviewEventResult {
  if (snapshot.reviewHistory.some((item) => item.eventId === event.eventId)) {
    return { ok: true, snapshot, duplicate: true };
  }
  if (event.format === "cards") {
    return applyCardEvent(snapshot, event);
  }
  return applyWrittenEvent(snapshot, event);
}

export function applyReviewEventOrThrow(snapshot: VocabularySnapshot, event: ReviewEvent): VocabularySnapshot {
  const result = applyReviewEvent(snapshot, event);
  if (!result.ok) {
    throw Object.assign(new Error(result.reason), { reason: result.reason });
  }
  return result.snapshot;
}

export function restorePreAssessmentSchedule(
  snapshot: VocabularySnapshot,
  assessmentEventId: string,
  correction: ReviewEvent,
): ApplyReviewEventResult {
  const assessment = snapshot.reviewHistory.find((item) => item.eventId === assessmentEventId);
  if (!assessment || assessment.format !== "written" || assessment.eventKind !== "assessment") {
    return { ok: false, reason: "missing_exercise" };
  }
  if (correction.format !== "written") {
    return { ok: false, reason: "missing_exercise" };
  }
  if (snapshot.reviewHistory.some((item) => item.eventId === correction.eventId)) {
    return { ok: true, snapshot, duplicate: true };
  }
  const current = practiceStateFor(snapshot, assessment.meaningId);
  if (current.stateRevision !== assessment.resultingSchedule.stateRevision) {
    return { ok: false, reason: "revision_mismatch" };
  }
  const restored: MeaningPracticeState = {
    ...current,
    stage: assessment.previousSchedule.stage,
    dueAt: assessment.previousSchedule.dueAt,
    lastAssessmentAt: assessment.previousSchedule.lastAssessmentAt,
    lastExerciseId: assessment.previousSchedule.lastExerciseId,
    lastExposureAt: current.lastExposureAt,
    flaggedExerciseIds:
      correction.exerciseId && !current.flaggedExerciseIds.includes(correction.exerciseId)
        ? [...current.flaggedExerciseIds, correction.exerciseId]
        : current.flaggedExerciseIds,
    stateRevision: current.stateRevision + 1,
  };
  if (isClockBackward(snapshot.schedulerState.lastObservedAt, correction.presentedAt, correction.completedAt)) {
    return { ok: false, reason: "clock_backward" };
  }
  const recorded: WrittenReviewEvent = {
    ...correction,
    eventKind: "invalidation",
    previousSchedule: scheduleSnapshotOf(current),
    resultingSchedule: scheduleSnapshotOf(restored),
  };
  const history = pruneHistory([...snapshot.reviewHistory, recorded], snapshot.historyTruncated);
  return {
    ok: true,
    duplicate: false,
    snapshot: snapshotWithDataRevision(snapshot, {
      words: snapshot.words,
      meanings: snapshot.meanings,
      relations: snapshot.relations,
      exerciseSets: snapshot.exerciseSets,
      practiceStates: snapshot.practiceStates.map((state) =>
        state.meaningId === restored.meaningId ? restored : state,
      ),
      schedulerState: {
        ...snapshot.schedulerState,
        lastObservedAt: snapshot.schedulerState.lastObservedAt
          ? maxInstant(snapshot.schedulerState.lastObservedAt, correction.completedAt)
          : correction.completedAt,
      },
      reviewHistory: history.reviewHistory,
      historyTruncated: history.historyTruncated,
    }),
  };
}
